import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { toast } from "sonner";
import { Package, Truck, CheckCircle2, X as XIcon, RotateCcw, AlertTriangle, Euro, FileDown, Mail, BarChart3 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const STATUS_STYLES = {
  paid:          { label: "Pagado",         bg: "bg-muted",     text: "text-navy" },
  picking:       { label: "Preparando",     bg: "bg-cream/40",  text: "text-navy" },
  shipped:       { label: "Enviado",        bg: "bg-navy",      text: "text-cream" },
  delivered:     { label: "Entregado",      bg: "bg-royal",     text: "text-cream" },
  cancelled:     { label: "Cancelado",      bg: "bg-muted",     text: "text-muted-foreground" },
  rma_requested: { label: "Devolución sol.",bg: "bg-cream/40",  text: "text-royal" },
  rma_approved:  { label: "Devolución OK",  bg: "bg-cream",     text: "text-royal" },
  refunded:      { label: "Reembolsado",    bg: "bg-royal/20",  text: "text-royal" },
};

const STATUS_LIST = Object.keys(STATUS_STYLES);
const CARRIERS = ["", "SEUR", "MRW", "Correos Express", "DHL", "UPS", "GLS", "InPost"];

export default function AdminOrders() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [debQ, setDebQ] = useState("");
  const [busy, setBusy] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setDebQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchList = async () => {
    if (!user?.is_admin) return;
    setBusy(true);
    const params = { limit: 50 };
    if (debQ) params.q = debQ;
    if (statusFilter) params.status = statusFilter;
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/admin/orders", { params }),
        api.get("/admin/orders/stats"),
      ]);
      setOrders(list.orders);
      setTotal(list.count);
      setStats(s);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, debQ, statusFilter]);

  if (loading || !user?.is_admin) {
    return <div className="p-24 text-center text-muted-foreground">Cargando…</div>;
  }

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12" data-testid="admin-orders-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-caps text-royal">Backoffice</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-2">Pedidos · {total}</h1>
          <div className="mt-3 flex items-center gap-3 font-mono-caps text-xs">
            <Link to="/admin" className="text-muted-foreground hover:text-navy editorial-link" data-testid="admin-nav-products">Productos</Link>
            <span className="text-navy">Pedidos</span>
            <Link to="/admin/analytics" className="text-muted-foreground hover:text-navy editorial-link flex items-center gap-1" data-testid="admin-nav-analytics">
              <BarChart3 strokeWidth={1.25} className="h-3 w-3" /> Analytics
            </Link>
            <Link to="/admin/ajustes/ups" className="text-muted-foreground hover:text-navy editorial-link flex items-center gap-1" data-testid="admin-nav-ups">
              <Truck strokeWidth={1.25} className="h-3 w-3" /> Ajustes UPS
            </Link>
          </div>
        </div>
        <div className="flex gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por email, cliente, #pedido"
            className="w-72 border border-border p-3 focus:border-navy outline-none"
            data-testid="orders-search"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-border p-3 focus:border-navy outline-none"
            data-testid="orders-status-filter"
          >
            <option value="">Todos los estados</option>
            {STATUS_LIST.map((s) => <option key={s} value={s}>{STATUS_STYLES[s].label}</option>)}
          </select>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="orders-stats">
          <StatCard label="Total pedidos" value={stats.total} icon={Package} />
          <StatCard label="Pagados" value={stats.paid} icon={Euro} />
          <StatCard label="Enviados" value={stats.shipped} icon={Truck} />
          <StatCard label="Entregados" value={stats.delivered} icon={CheckCircle2} />
          <StatCard label="Ingresos" value={`$${stats.revenue.toFixed(2)}`} icon={Euro} highlight />
        </div>
      )}

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="bg-muted font-mono-caps text-xs">
            <tr>
              <th className="text-left px-4 py-3">#Pedido</th>
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-left px-4 py-3">Cliente</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-right px-4 py-3">Items</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Estado</th>
              <th className="text-left px-4 py-3">Tracking</th>
              <th className="px-4 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {busy && (<tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">Cargando…</td></tr>)}
            {!busy && orders.length === 0 && (<tr><td colSpan={9} className="px-4 py-16 text-center text-muted-foreground">Sin pedidos</td></tr>)}
            {!busy && orders.map((o) => {
              const st = STATUS_STYLES[o.status] || { label: o.status, bg: "bg-muted", text: "text-foreground" };
              const d = o.created_at ? new Date(o.created_at) : null;
              return (
                <tr key={o.order_id} className="border-t border-border hover:bg-muted/40" data-testid={`order-row-${o.order_id}`}>
                  <td className="px-4 py-3 font-mono-caps text-xs">{o.order_id}</td>
                  <td className="px-4 py-3 text-xs">{d ? d.toLocaleDateString("es-ES") : "—"}</td>
                  <td className="px-4 py-3 font-serif">{o.customer_name || "—"}</td>
                  <td className="px-4 py-3 text-xs">{o.email || "—"}</td>
                  <td className="px-4 py-3 text-right">{o.items?.length || 0}</td>
                  <td className="px-4 py-3 text-right font-medium">${o.amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono-caps text-[10px] px-2 py-1 ${st.bg} ${st.text}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {o.tracking_number ? `${o.shipping_carrier || ""} ${o.tracking_number}`.trim() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setOpenId(o.order_id)}
                      className="font-mono-caps text-xs border border-navy px-3 py-1 hover:bg-navy hover:text-cream transition-colors"
                      data-testid={`order-open-${o.order_id}`}
                    >
                      Abrir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <OrderDrawer orderId={openId} onClose={() => setOpenId(null)} onUpdated={fetchList} />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, highlight }) {
  return (
    <div className={`border border-border p-4 ${highlight ? "bg-navy text-cream border-navy" : ""}`}>
      <div className="flex items-center gap-2 font-mono-caps text-[10px] opacity-80">
        <Icon strokeWidth={1.25} className="h-3 w-3" /> {label}
      </div>
      <div className="font-serif text-2xl mt-2">{value}</div>
    </div>
  );
}

function OrderDrawer({ orderId, onClose, onUpdated }) {
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState([]);

  useEffect(() => {
    if (!orderId) { setOrder(null); setEmails([]); return; }
    api.get(`/admin/orders/${orderId}`).then(({ data }) => {
      setOrder(data);
      setForm({
        status: data.status || "paid",
        tracking_number: data.tracking_number || "",
        shipping_carrier: data.shipping_carrier || "",
        notes: data.notes || "",
        rma_reason: data.rma_reason || "",
      });
    });
    api.get(`/admin/emails`, { params: { order_id: orderId } }).then(({ data }) => setEmails(data));
  }, [orderId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/orders/${orderId}`, form);
      setOrder(data);
      toast.success("Pedido actualizado");
      onUpdated?.();
      const { data: mails } = await api.get(`/admin/emails`, { params: { order_id: orderId } });
      setEmails(mails);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const downloadLabel = async () => {
    try {
      const token = localStorage.getItem("pf_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}/label.pdf`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!resp.ok) throw new Error("PDF error");
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `etiqueta-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("Etiqueta descargada");
    } catch {
      toast.error("Error al generar la etiqueta");
    }
  };

  const downloadUpsLabel = async () => {
    try {
      const token = localStorage.getItem("pf_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}/ups-label`;
      const resp = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ service_code: "03", label_format: "PDF" }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: "UPS error" }));
        throw new Error(err.detail?.message || err.detail || "UPS error");
      }
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `ups-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("Etiqueta UPS generada");
      onUpdated?.();
    } catch (e) {
      toast.error(e.message || "Error al generar etiqueta UPS");
    }
  };

  const requestRma = () => setForm((f) => ({ ...f, status: "rma_requested" }));
  const approveRma = () => setForm((f) => ({ ...f, status: "rma_approved" }));

  const stripeRefund = async () => {
    if (!order) return;
    const amtStr = window.prompt(
      `Reembolso vía Stripe.\n\nDeja en blanco para reembolso TOTAL de $${order.amount?.toFixed(2)}.\nO escribe un importe parcial (USD).`,
      ""
    );
    if (amtStr === null) return;
    const amount = amtStr.trim() === "" ? null : Number(amtStr);
    if (amount !== null && (!Number.isFinite(amount) || amount <= 0)) {
      toast.error("Importe inválido");
      return;
    }
    const reason = window.prompt(
      "Motivo (opcional). Valores válidos para Stripe:\nrequested_by_customer, duplicate, fraudulent.\nDéjalo vacío si no aplica.",
      "requested_by_customer"
    );
    if (!window.confirm(`Confirmar reembolso ${amount === null ? "TOTAL" : `de $${amount.toFixed(2)}`} para ${order.order_id}?`)) return;
    try {
      const { data } = await api.post(`/admin/orders/${order.order_id}/refund`, {
        amount,
        reason: reason || undefined,
      });
      toast.success(`Reembolso ${data.status === "refunded" ? "total" : "parcial"} procesado`, {
        description: `Stripe refund_id: ${data.refund_id}`,
      });
      onUpdated?.();
      onClose();
    } catch (e) {
      toast.error("Error en el reembolso", { description: e?.response?.data?.detail || String(e) });
    }
  };

  return (
    <Sheet open={!!orderId} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none border-l overflow-y-auto p-0" data-testid="order-drawer">
        <SheetHeader className="px-6 py-5 border-b sticky top-0 bg-background z-10">
          <SheetTitle className="font-mono-caps text-left">{order ? `Pedido ${order.order_id}` : "Cargando…"}</SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground text-left">
            {order ? new Date(order.created_at).toLocaleString("es-ES") : "Detalles del pedido"}
          </SheetDescription>
        </SheetHeader>
        {order && (
          <>
            <div className="p-6 space-y-6">
              <section>
                <div className="font-mono-caps text-xs text-muted-foreground mb-2">Cliente</div>
                <div className="font-serif text-xl">{order.customer_name}</div>
                <div className="text-sm text-muted-foreground">{order.email}</div>
              </section>

              <section>
                <div className="font-mono-caps text-xs text-muted-foreground mb-3">Artículos ({order.items?.length})</div>
                <ul className="border border-border divide-y">
                  {order.items?.map((it, i) => (
                    <li key={i} className="flex justify-between px-4 py-3 text-sm">
                      <div>
                        <div className="font-serif">{it.name}</div>
                        <div className="text-xs text-muted-foreground font-mono-caps">{it.brand} · {it.size_ml}ml · x{it.qty}</div>
                      </div>
                      <div className="font-medium">${(it.unit_price * it.qty).toFixed(2)}</div>
                    </li>
                  ))}
                  <li className="flex justify-between px-4 py-3 bg-muted font-serif">
                    <span>Total</span>
                    <span>${order.amount?.toFixed(2)}</span>
                  </li>
                </ul>
              </section>

              <section className="space-y-4">
                <div className="font-mono-caps text-xs text-muted-foreground">Gestión</div>
                <div className="grid grid-cols-2 gap-4">
                  <label className="block">
                    <span className="block text-xs text-muted-foreground mb-1">Estado</span>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="w-full border border-border p-3 focus:border-navy outline-none"
                      data-testid="order-status-select"
                    >
                      {STATUS_LIST.map((s) => <option key={s} value={s}>{STATUS_STYLES[s].label}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-xs text-muted-foreground mb-1">Transportista</span>
                    <select
                      value={form.shipping_carrier}
                      onChange={(e) => setForm({ ...form, shipping_carrier: e.target.value })}
                      className="w-full border border-border p-3 focus:border-navy outline-none"
                      data-testid="order-carrier-select"
                    >
                      {CARRIERS.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
                    </select>
                  </label>
                </div>
                <label className="block">
                  <span className="block text-xs text-muted-foreground mb-1">Número de seguimiento</span>
                  <input
                    value={form.tracking_number}
                    onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                    className="w-full border border-border p-3 focus:border-navy outline-none"
                    data-testid="order-tracking-input"
                    placeholder="Ej. SEUR-2026-000123"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs text-muted-foreground mb-1">Notas internas</span>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    className="w-full border border-border p-3 focus:border-navy outline-none resize-y"
                    data-testid="order-notes"
                  />
                </label>
              </section>

              <section className="border-t border-border pt-6 space-y-3">
                <div className="font-mono-caps text-xs text-royal">Devoluciones (RMA)</div>
                <label className="block">
                  <span className="block text-xs text-muted-foreground mb-1">Motivo</span>
                  <input
                    value={form.rma_reason}
                    onChange={(e) => setForm({ ...form, rma_reason: e.target.value })}
                    className="w-full border border-border p-3 focus:border-navy outline-none"
                    data-testid="order-rma-reason"
                    placeholder="Cliente no satisfecho con la fragancia"
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <button onClick={requestRma} className="flex items-center gap-2 font-mono-caps text-xs border border-royal px-3 py-2 hover:bg-royal hover:text-cream transition-colors" data-testid="order-rma-request">
                    <AlertTriangle strokeWidth={1.25} className="h-3 w-3" /> Solicitar devolución
                  </button>
                  <button onClick={approveRma} className="flex items-center gap-2 font-mono-caps text-xs border border-navy px-3 py-2 hover:bg-navy hover:text-cream transition-colors" data-testid="order-rma-approve">
                    <RotateCcw strokeWidth={1.25} className="h-3 w-3" /> Aprobar devolución
                  </button>
                  <button onClick={stripeRefund} className="flex items-center gap-2 font-mono-caps text-xs bg-[#8B0000] text-cream px-3 py-2 hover:bg-navy transition-colors" data-testid="order-refund">
                    <RotateCcw strokeWidth={1.25} className="h-3 w-3" /> Reembolsar (Stripe)
                  </button>
                </div>
              </section>

              <section className="border-t border-border pt-6 space-y-3">
                <div className="font-mono-caps text-xs text-navy flex items-center gap-2">
                  <FileDown strokeWidth={1.25} className="h-3 w-3" /> Documentación de envío
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={downloadUpsLabel}
                    className="flex items-center gap-2 font-mono-caps text-xs bg-navy text-cream px-3 py-2 hover:bg-royal transition-colors"
                    data-testid="order-download-ups-label"
                  >
                    <Truck strokeWidth={1.25} className="h-3 w-3" /> Generar etiqueta UPS
                  </button>
                  <button
                    onClick={downloadLabel}
                    className="flex items-center gap-2 font-mono-caps text-xs border border-navy px-3 py-2 hover:bg-navy hover:text-cream transition-colors"
                    data-testid="order-download-label"
                  >
                    <FileDown strokeWidth={1.25} className="h-3 w-3" /> Etiqueta genérica (fallback)
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  UPS reemplaza la etiqueta genérica cuando las credenciales están configuradas en <Link to="/admin/ajustes/ups" className="underline hover:text-navy">Ajustes → UPS</Link>.
                </p>
              </section>

              {emails.length > 0 && (
                <section className="border-t border-border pt-6 space-y-3" data-testid="order-emails">
                  <div className="font-mono-caps text-xs text-navy flex items-center gap-2">
                    <Mail strokeWidth={1.25} className="h-3 w-3" /> Emails enviados ({emails.length})
                  </div>
                  {emails.map((m, i) => (
                    <div key={i} className="border border-border p-3 text-xs bg-muted/40">
                      <div className="flex justify-between font-mono-caps">
                        <span>{m.template} · {new Date(m.sent_at).toLocaleString("es-ES")}</span>
                        <span className="text-royal">MOCKED</span>
                      </div>
                      <div className="mt-1 font-serif text-sm">{m.subject}</div>
                      <pre className="mt-2 whitespace-pre-wrap text-muted-foreground text-[11px] leading-relaxed">{m.body}</pre>
                    </div>
                  ))}
                </section>
              )}

              <div className="flex gap-3 sticky bottom-0 bg-background pt-4 border-t border-border">
                <button onClick={onClose} className="flex-1 border border-border px-6 py-3 font-mono-caps hover:border-navy transition-colors" data-testid="order-close">
                  Cerrar
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="flex-1 bg-navy text-cream px-6 py-3 font-mono-caps hover:bg-cream hover:text-navy transition-colors disabled:opacity-50"
                  data-testid="order-save"
                >
                  {saving ? "Guardando…" : "Guardar cambios"}
                </button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
